import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileUp, ClipboardList } from "lucide-react";

const STATUS_STYLE = {
  sent: "bg-[#E6ECF5] text-[#1B4383]",
  quoted: "bg-[#E4EEE8] text-[#527D63]",
  costing_done: "bg-[#DCE8F5] text-[#1B4383] font-semibold",
  awarded: "bg-[#F6EDD9] text-[#A87F31]",
  declined: "bg-slate-100 text-slate-400",
};
const STATUS_LABEL = { sent: "Sent RFQ", quoted: "Quote Received", costing_done: "Costing Done", awarded: "Awarded", declined: "RFQ Declined" };
const prettyStatus = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function QuotesPanel({ rfqId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);

  const load = useCallback(() => api.get(`/rfqs/${rfqId}/quote-suppliers`).then((r) => setRows(r.data)), [rfqId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-lg border border-border bg-white">
      <div className="border-b border-border px-5 py-4">
        <h2 className="font-heading text-base font-bold tracking-tight">Supplier Quotes and Costing</h2>
        <p className="text-sm text-muted-foreground">Suppliers who were sent this RFQ. Open a supplier to record prices, lead times, terms and store their quote document.</p>
      </div>
      {/* Desktop table */}
      <table className="data-table hidden w-full text-sm sm:table">
        <thead>
          <tr className="bg-slate-50 text-left text-xs tracking-wide text-slate-500">
            <th className="px-5 py-3 font-medium">Supplier</th>
            <th className="px-5 py-3 font-medium text-center">Status</th>
            <th className="px-5 py-3 font-medium text-center">Priced</th>
            <th className="px-5 py-3 font-medium">Terms</th>
            <th className="px-5 py-3 font-medium text-right">Quote</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.supplier_id} className="border-t border-border hover:bg-slate-50" data-testid={`quote-supplier-${r.supplier_id}`}>
              <td className="px-5 py-3 font-medium text-slate-900">{r.supplier_name}</td>
              <td className="px-5 py-3 text-center">
                <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{STATUS_LABEL[r.status] || prettyStatus(r.status)}</span>
              </td>
              <td className="px-5 py-3 text-center font-mono text-xs">{r.priced_count}/{r.item_count}</td>
              <td className="px-5 py-3 text-muted-foreground text-xs">
                {r.has_quote ? [r.currency, r.payment_terms, r.validity].filter(Boolean).join(" · ") || "—" : "—"}
              </td>
              <td className="px-5 py-3 text-right">
                <Button size="sm" variant={r.has_quote ? "outline" : "default"} onClick={() => navigate(`/rfqs/${rfqId}/quotes/${r.supplier_id}`)} data-testid={`open-quote-${r.supplier_id}`}>
                  {r.has_quote ? <ClipboardList className="mr-2 h-4 w-4" /> : <FileUp className="mr-2 h-4 w-4" />}
                  {!r.has_quote ? "Add Quote" : (r.item_count > 0 && r.priced_count >= r.item_count ? "View / Edit" : "Continue Costing")}
                </Button>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No suppliers attached yet. Attach suppliers to line items first.</td></tr>}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="divide-y divide-border sm:hidden">
        {rows.map((r) => (
          <div key={r.supplier_id} className="p-4" data-testid={`quote-supplier-m-${r.supplier_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="break-words font-medium text-slate-900">{r.supplier_name}</div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{STATUS_LABEL[r.status] || prettyStatus(r.status)}</span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-mono text-xs text-slate-600">{r.priced_count}/{r.item_count} priced</span>
                </div>
              </div>
            </div>
            {r.has_quote && ([r.currency, r.payment_terms, r.validity].filter(Boolean).length > 0) && (
              <div className="mt-2 text-xs text-muted-foreground">{[r.currency, r.payment_terms, r.validity].filter(Boolean).join(" · ")}</div>
            )}
            <Button size="sm" className="mt-3 w-full" variant={r.has_quote ? "outline" : "default"} onClick={() => navigate(`/rfqs/${rfqId}/quotes/${r.supplier_id}`)} data-testid={`open-quote-m-${r.supplier_id}`}>
              {r.has_quote ? <ClipboardList className="mr-2 h-4 w-4" /> : <FileUp className="mr-2 h-4 w-4" />}
              {!r.has_quote ? "Add Quote" : (r.item_count > 0 && r.priced_count >= r.item_count ? "View / Edit" : "Continue Costing")}
            </Button>
          </div>
        ))}
        {!rows.length && <div className="px-5 py-10 text-center text-muted-foreground">No suppliers attached yet. Attach suppliers to line items first.</div>}
      </div>
    </div>
  );
}
